import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'

const SearchableSelect = ({
  value = null,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  clearable = false,
  clearLabel = 'None',
  searchable = true,
  multiple = false,
  disabled = false,
  autoOpen = false,
  onClose,
  ariaLabel,
  id,
  className,
}) => {
  const [open, setOpen] = useState(!!autoOpen)
  const wasOpen = useRef(!!autoOpen)
  const [coords, setCoords] = useState(null)
  const [portalTarget, setPortalTarget] = useState(null)
  const [search, setSearch] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)
  const btnRef = useRef(null)
  const popRef = useRef(null)
  const searchRef = useRef(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const values = multiple ? (Array.isArray(value) ? value : []) : value
  const isChosen = v => (multiple
    ? values.some(x => String(x) === String(v))
    : String(v) === String(value))

  const selectedOption = useMemo(
    () => (multiple ? null : options.find(o => String(o.value) === String(value)) || null),
    [options, value, multiple]
  )

  const selectedLabel = useMemo(() => {
    if (multiple) {
      const chosen = options.filter(o => values.some(x => String(x) === String(o.value)))
      if (chosen.length === 0) return null
      if (chosen.length === 1) return chosen[0].label
      return `${chosen.length} selected`
    }
    return selectedOption?.label ?? null
  }, [selectedOption, values, options, multiple])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return options
    return options.filter(o => `${o.label ?? ''} ${o.sublabel ?? ''} ${o.group ?? ''}`.toLowerCase().includes(q))
  }, [options, search])

  const rows = (clearable && !multiple)
    ? [{ value: null, label: clearLabel, __clear: true }, ...filtered]
    : filtered

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const width = Math.max(r.width, 220)
      const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12))
      const top = Math.min(r.bottom + 6, window.innerHeight - 340)
      setCoords({ top, left, width })
      // Portal into the enclosing MUI dialog when there is one, so the search
      // box stays inside the dialog's focus trap (typing works); otherwise
      // portal to body. Positioning is viewport-fixed either way.
      setPortalTarget(btnRef.current.closest('.MuiDialog-paper') || document.body)
    }
  }, [open])

  useEffect(() => {
    if (open) { wasOpen.current = true } else if (wasOpen.current) { wasOpen.current = false; onCloseRef.current?.() }
    if (!open) { setSearch(''); setActiveIndex(-1); return }
    const t = setTimeout(() => searchRef.current?.focus(), 20)
    return () => clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const pick = o => {
    if (o.disabled) return
    if (multiple) {
      const has = values.some(x => String(x) === String(o.value))
      onChange?.(has ? values.filter(x => String(x) !== String(o.value)) : [...values, o.value])
    } else {
      onChange?.(o.value)
      setOpen(false)
    }
  }

  const onKeyDown = e => {
    if (e.key === 'Escape') { setOpen(false); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, rows.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (rows[activeIndex]) pick(rows[activeIndex]) }
  }

  let lastGroup = null

  return (
    <>
      <Trigger
        ref={btnRef}
        type='button'
        id={id}
        className={className}
        disabled={disabled}
        aria-label={ariaLabel || placeholder}
        aria-haspopup='listbox'
        aria-expanded={open}
        $empty={!selectedLabel}
        onClick={() => !disabled && setOpen(o => !o)}
      >
        {selectedOption?.color && <Dot style={{ background: selectedOption.color }} />}
        <TriggerText $empty={!selectedLabel}>{selectedLabel || placeholder}</TriggerText>
        <ExpandMoreRoundedIcon style={{ fontSize: 18, flexShrink: 0, opacity: 0.55 }} />
      </Trigger>

      {open && coords && createPortal(
        <Pop
          ref={popRef}
          role='listbox'
          aria-label={ariaLabel || placeholder}
          style={{ top: coords.top, left: coords.left, width: coords.width }}
          onKeyDown={onKeyDown}
        >
          {searchable && (
            <SearchRow>
              <SearchRoundedIcon style={{ fontSize: 16, color: '#9aa0ab' }} />
              <SearchInput
                ref={searchRef}
                value={search}
                placeholder={searchPlaceholder}
                onChange={e => { setSearch(e.target.value); setActiveIndex(-1) }}
              />
            </SearchRow>
          )}
          <List>
            {rows.length === 0 && <Empty>No matches.</Empty>}
            {rows.map((o, i) => {
              const sel = !o.__clear && isChosen(o.value)
              const showGroup = !o.__clear && o.group && o.group !== lastGroup
              if (!o.__clear) lastGroup = o.group || lastGroup
              return (
                <React.Fragment key={o.__clear ? '__clear' : String(o.value)}>
                  {showGroup && <GroupHeader>{o.group}</GroupHeader>}
                  <Row
                    type='button'
                    role='option'
                    aria-selected={sel}
                    aria-disabled={o.disabled || undefined}
                    $active={i === activeIndex}
                    $selected={sel}
                    $disabled={!!o.disabled}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => pick(o)}
                  >
                    {multiple && <Box $on={sel}>{sel ? '✓' : ''}</Box>}
                    {o.color && <Dot style={{ background: o.color }} />}
                    <RowText>
                      <RowLabel $muted={!!o.__clear}>{o.label}</RowLabel>
                      {o.sublabel && <RowSub>{o.sublabel}</RowSub>}
                    </RowText>
                    {!multiple && sel && <CheckMark>✓</CheckMark>}
                  </Row>
                </React.Fragment>
              )
            })}
          </List>
        </Pop>,
        portalTarget || document.body
      )}
    </>
  )
}

export default SearchableSelect

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #d7dbe3;
  border-radius: 9px;
  background: #fff;
  color: #1c2029;
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: border-color 120ms ease;

  &:hover:not(:disabled) { border-color: #33becc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 1px; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const TriggerText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $empty }) => ($empty ? '#8a90a0' : '#1c2029')};
`

const Pop = styled.div`
  position: fixed;
  z-index: 100060;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  box-shadow: 0 18px 44px -22px rgba(12, 18, 41, 0.4);
`

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border: 1px solid #e2e6ec;
  border-radius: 9px;
`

const SearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font: inherit;
  font-size: 13px;
  background: transparent;
  color: #1c2029;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 244px;
  overflow-y: auto;
`

const GroupHeader = styled.div`
  padding: 8px 10px 4px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9aa0ab;
`

const Row = styled.button`
  font: inherit;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  min-height: 40px;
  background: ${({ $active, $selected }) => ($selected ? '#f2fbfb' : $active ? '#f7f8fa' : 'transparent')};
`

const Box = styled.span`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
  border: 1.5px solid ${({ $on }) => ($on ? '#2aa6b3' : '#cfd4dd')};
  background: ${({ $on }) => ($on ? '#2aa6b3' : '#fff')};
`

const Dot = styled.span`
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #cfd4dd;
`

const RowText = styled.div`
  flex: 1;
  min-width: 0;
`

const RowLabel = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ $muted }) => ($muted ? '#8a90a0' : '#1c2029')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const RowSub = styled.div`
  font-size: 11.5px;
  color: #8a90a0;
`

const CheckMark = styled.span`
  color: #2aa6b3;
  font-weight: 700;
  flex-shrink: 0;
`

const Empty = styled.div`
  font-size: 12.5px;
  color: #8a90a0;
  padding: 10px 6px;
`
